// Best-effort viewport screenshot for the talent platform's debug mode — the
// talent-side twin of the buyer app's `web/src/lib/debug-screenshot.ts`. When a
// tester logs a debug note (red "Debug" toggle ON) we capture what they were
// looking at with html2canvas and attach it to the note. Capture is best-effort
// and bounded: any failure or a timeout resolves to `null` so the note still
// saves (doctrine 1.5 — never throw, degrade). The caller "waits for the shot"
// only on the happy path.

import html2canvas from "html2canvas";

// Keep IMAGE_TIMEOUT_MS strictly inside CAPTURE_TIMEOUT_MS (Monday 3180339343).
// html2canvas loads every <img> in the clone and drops a single image only after
// `imageTimeout` (default 15s), so without a per-image budget under the overall
// cap one stalled image loses the whole shot. Full story in the buyer app's
// web/src/lib/debug-screenshot.ts.
export const CAPTURE_TIMEOUT_MS = 12_000;
export const IMAGE_TIMEOUT_MS = 3_000;
// JPEG keeps the upload small (a typical viewport lands well under the server's
// cap); scale 1 keeps it readable for spotting visual bugs.
const JPEG_QUALITY = 0.7;

// html2canvas 1.x THROWS ("Attempting to parse an unsupported color function") on
// color functions it does not know, and Chrome serializes `color-mix(...)` and
// oklch/oklab/lab/lch into `color(srgb …)` / `oklab(…)` in computed style. The
// shared `--rc-*` token tiers can carry such functions, and one such value loses
// the whole capture, so the `onclone` hook resolves every unsupported computed
// color to plain `rgba()` first, as the buyer app does.
// The scanner is paren-BALANCED, not a flat regex: an unresolved `color-mix()`
// (Firefox, or any engine with a `currentcolor` argument) nests `rgb()` calls.
const COLOR_FN_START = /(?<![\w-])(?:color-mix|color|oklab|oklch|lab|lch|hwb|light-dark)\(/gi;
const HAS_UNSUPPORTED_COLOR_FN = /(?<![\w-])(?:color-mix|color|oklab|oklch|lab|lch|hwb|light-dark)\(/i;
// EVERY property html2canvas 1.4.1 runs its colour parser on (read off its dist:
// the `format: "color"` descriptors plus the shadow lists and gradient stops).
// A compound value has each colour-function occurrence rewritten in place.
// `outline-`, `column-rule-`, `caret-color`, `fill` and `stroke` are not parsed by
// html2canvas at all; they stay as cheap insurance against a future upgrade.
const SANITIZED_COLOR_PROPS = [
  "color",
  "background-color",
  "border-top-color",
  "border-right-color",
  "border-bottom-color",
  "border-left-color",
  "text-decoration-color",
  "-webkit-text-stroke-color",
  "outline-color",
  "column-rule-color",
  "caret-color",
  "fill",
  "stroke",
  "text-shadow",
  "box-shadow",
  "background-image",
];

/**
 * Replace each unsupported color-function call in `value` using `toRgba`
 * (which converts one whole `color(…)`/`color-mix(…)`/`oklch(…)`/… call to
 * `rgba(…)`). A call is taken as ONE balanced token, nested parentheses
 * included. Pure — the rewriting logic is unit-tested with a stub `toRgba`; the
 * real conversion is the browser rasterizer below. Tokens `toRgba` leaves
 * unchanged are kept; an unbalanced tail is left untouched.
 */
export function replaceUnsupportedColors(value: string, toRgba: (fn: string) => string): string {
  let out = "";
  let cursor = 0;
  COLOR_FN_START.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = COLOR_FN_START.exec(value)) !== null) {
    const start = match.index;
    let depth = 0;
    let end = -1;
    for (let j = start + match[0].length - 1; j < value.length; j++) {
      const ch = value[j];
      if (ch === "(") depth += 1;
      else if (ch === ")") {
        depth -= 1;
        if (depth === 0) {
          end = j;
          break;
        }
      }
    }
    if (end === -1) break;
    out += value.slice(cursor, start) + toRgba(value.slice(start, end + 1));
    cursor = end + 1;
    COLOR_FN_START.lastIndex = cursor;
  }
  return out + value.slice(cursor);
}

/**
 * Build a resolver that turns any CSS color string — including the wide-gamut
 * `color()`/`oklab()`/`oklch()`/`lab()`/`lch()` functions — into an sRGB
 * `rgba(…)` html2canvas can parse. It rasterizes the color into a 1×1 canvas and
 * reads back the pixel (the only reliable browser-native down-conversion: the
 * `fillStyle` getter and `getComputedStyle` both preserve the wide-gamut form).
 * The 1×1 canvas only ever paints solid colors, so it never taints.
 */
function makeColorResolver(): (value: string, currentColor: string) => string {
  const ctx = document.createElement("canvas").getContext("2d");
  return (value: string, currentColor: string): string => {
    if (!ctx) return value;
    return replaceUnsupportedColors(value, (fn) => {
      // The 1×1 canvas is not in the DOM, so `currentcolor` there would mean
      // black; resolve it against the element's own computed colour first.
      const token =
        currentColor && /currentcolor/i.test(fn) ? fn.replace(/currentcolor/gi, currentColor) : fn;
      try {
        ctx.clearRect(0, 0, 1, 1);
        ctx.fillStyle = "#000";
        ctx.fillStyle = token; // ignored if genuinely unparseable → stays #000
        ctx.fillRect(0, 0, 1, 1);
        const [r, g, b, a] = ctx.getImageData(0, 0, 1, 1).data;
        return `rgba(${r}, ${g}, ${b}, ${(a / 255).toFixed(4)})`;
      } catch {
        return fn;
      }
    });
  };
}

/**
 * Walk the cloned document and replace any computed color that uses a function
 * html2canvas can't parse with an equivalent `rgba(…)`, set inline (with
 * `important` so it wins over the stylesheet). Best-effort: never throws.
 */
export function resolveUnsupportedColors(doc: Document): void {
  const view = doc.defaultView ?? window;
  const resolve = makeColorResolver();
  doc.querySelectorAll<HTMLElement>("*").forEach((el) => {
    const cs = view.getComputedStyle(el);
    const currentColor = cs.getPropertyValue("color");
    for (const prop of SANITIZED_COLOR_PROPS) {
      const v = cs.getPropertyValue(prop);
      if (v && HAS_UNSUPPORTED_COLOR_FN.test(v)) {
        el.style.setProperty(prop, resolve(v, prop === "color" ? "" : currentColor), "important");
      }
    }
  });
}

/**
 * Drop the gradient background of every box the clone lays out at exactly 0×0.
 * html2canvas 1.4.1 divides each gradient stop by the gradient line length and
 * for a 0×0 box that length is exactly 0: the stops come out NaN and the
 * canvas's `addColorStop(NaN)` throws "The provided double value is non-finite",
 * which aborts the WHOLE capture (reproduced in Chrome 148, 2026-09-08). A 0×0
 * box paints nothing, so removing its gradient changes no pixel. Best-effort.
 */
export function neutralizeZeroSizeGradients(doc: Document): void {
  const view = doc.defaultView ?? window;
  doc.querySelectorAll<HTMLElement>("*").forEach((el) => {
    const cs = view.getComputedStyle(el);
    if (cs.getPropertyValue("display") === "none") return;
    const bg = cs.getPropertyValue("background-image");
    if (!bg || !bg.includes("gradient(")) return;
    const rect = el.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) {
      el.style.setProperty("background-image", "none", "important");
    }
  });
}

/**
 * Jump every CSS animation in the clone to its settled end state and drop
 * transitions. The clone is a fresh document that loads the same stylesheets,
 * so every animation RESTARTS from its first keyframe there whatever the live
 * page shows; a large negative delay lands each one on its final frame, which
 * is the state the tester actually sees.
 */
export function settleAnimations(doc: Document): void {
  const style = doc.createElement("style");
  style.textContent =
    "*, *::before, *::after { animation-delay: -1000s !important; transition: none !important; }";
  doc.head?.appendChild(style);
}

function withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error("screenshot timed out")), ms);
    p.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });
}

/**
 * Capture the visible viewport (what the tester currently sees) as a JPEG Blob,
 * or `null` if capture fails or times out. Never throws.
 */
export async function captureViewport(): Promise<Blob | null> {
  try {
    const canvas = await withTimeout(
      html2canvas(document.body, {
        useCORS: true,
        logging: false,
        scale: 1,
        // Per-image budget, strictly inside the overall cap (see the constants).
        imageTimeout: IMAGE_TIMEOUT_MS,
        // Capture only the on-screen region (not the full scroll height).
        x: window.scrollX,
        y: window.scrollY,
        width: window.innerWidth,
        height: window.innerHeight,
        windowWidth: document.documentElement.clientWidth,
        windowHeight: document.documentElement.clientHeight,
        // Make the clone something html2canvas 1.x can render without throwing:
        // settle animations first (so the measurements below see final boxes),
        // then down-convert every colour function it cannot parse, then drop
        // the gradients it would turn into NaN. Each step is best-effort on its
        // own so one failing never costs the others.
        onclone: (cloned: Document) => {
          try {
            settleAnimations(cloned);
          } catch (err) {
            console.warn("debug screenshot animation settle failed", err);
          }
          try {
            resolveUnsupportedColors(cloned);
          } catch (err) {
            console.warn("debug screenshot color resolve failed", err);
          }
          try {
            neutralizeZeroSizeGradients(cloned);
          } catch (err) {
            console.warn("debug screenshot gradient neutralize failed", err);
          }
        },
      }),
      CAPTURE_TIMEOUT_MS,
    );
    return await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((blob) => resolve(blob), "image/jpeg", JPEG_QUALITY);
    });
  } catch (err) {
    // Degrade quietly: the note will save without an image.
    console.warn("debug screenshot capture failed", err);
    return null;
  }
}
